import { useState, useRef, useCallback } from 'react';
import { Button } from '@/ui/base/button';
import { Textarea } from '@/ui/base/textarea';
import { Save, X, Eye, Code, Type } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import type { DocArticleCompleto } from '@/lib/useDocs';
import { DocsRichEditor, type DocsRichEditorRef } from './DocsRichEditor';

type EditorMode = 'visual' | 'markdown' | 'preview';

interface DocsEditorProps {
  article: DocArticleCompleto;
  onSave: (markdown: string) => Promise<void>;
  onCancel: () => void;
  saving?: boolean;
}

export function DocsEditor({ article, onSave, onCancel, saving }: DocsEditorProps) {
  const [markdown, setMarkdown] = useState(article.conteudo_markdown || '');
  const [mode, setMode] = useState<EditorMode>('visual');
  const richEditorRef = useRef<DocsRichEditorRef>(null);

  const switchMode = useCallback(
    (newMode: EditorMode) => {
      // Sync content between modes
      if (mode === 'visual' && newMode !== 'visual' && richEditorRef.current) {
        const md = richEditorRef.current.getMarkdown();
        setMarkdown(md);
      } else if (mode === 'markdown' && newMode === 'visual' && richEditorRef.current) {
        richEditorRef.current.setFromMarkdown(markdown);
      }
      setMode(newMode);
    },
    [mode, markdown],
  );

  const handleSave = async () => {
    let content = markdown;
    if (mode === 'visual' && richEditorRef.current) {
      content = richEditorRef.current.getMarkdown();
    }
    await onSave(content);
  };

  return (
    <div className="flex-1 flex flex-col overflow-hidden">
      {/* Toolbar */}
      <div className="border-b border-border px-4 py-2 flex items-center justify-between bg-muted/30">
        <div className="flex items-center gap-2">
          <h2 className="text-sm font-medium text-foreground truncate max-w-[200px]">
            Editando: {article.titulo}
          </h2>
          <div className="flex items-center rounded-lg border border-border bg-background p-0.5">
            <Button
              variant={mode === 'visual' ? 'default' : 'ghost'}
              size="sm"
              onClick={() => switchMode('visual')}
              className="gap-1.5 h-7 text-xs"
            >
              <Type className="h-3.5 w-3.5" />
              Visual
            </Button>
            <Button
              variant={mode === 'markdown' ? 'default' : 'ghost'}
              size="sm"
              onClick={() => switchMode('markdown')}
              className="gap-1.5 h-7 text-xs"
            >
              <Code className="h-3.5 w-3.5" />
              Markdown
            </Button>
            <Button
              variant={mode === 'preview' ? 'default' : 'ghost'}
              size="sm"
              onClick={() => switchMode('preview')}
              className="gap-1.5 h-7 text-xs"
            >
              <Eye className="h-3.5 w-3.5" />
              Visualizar
            </Button>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={onCancel} disabled={saving}>
            <X className="h-3.5 w-3.5 mr-1" /> Cancelar
          </Button>
          <Button size="sm" onClick={handleSave} disabled={saving} className="gap-1.5">
            <Save className="h-3.5 w-3.5" />
            {saving ? 'Salvando...' : 'Salvar'}
          </Button>
        </div>
      </div>

      {/* Editor / Preview */}
      <div className="flex-1 overflow-auto">
        {mode === 'preview' ? (
          <div className="px-6 py-6 max-w-4xl">
            <article
              className="prose prose-sm dark:prose-invert max-w-none
              prose-headings:text-foreground prose-h1:text-2xl prose-h2:text-xl prose-h3:text-lg
              prose-p:text-foreground/80 prose-code:text-primary prose-code:bg-muted prose-code:px-1.5 prose-code:py-0.5 prose-code:rounded-md prose-code:text-xs
              prose-pre:bg-muted prose-pre:border prose-pre:border-border prose-pre:rounded-xl
              prose-th:bg-muted prose-th:border prose-th:border-border prose-th:px-3 prose-th:py-2
              prose-td:border prose-td:border-border prose-td:px-3 prose-td:py-2
            "
            >
              <ReactMarkdown remarkPlugins={[remarkGfm]}>
                {mode === 'preview' && richEditorRef.current
                  ? richEditorRef.current.getMarkdown()
                  : markdown}
              </ReactMarkdown>
            </article>
          </div>
        ) : mode === 'markdown' ? (
          <Textarea
            value={markdown}
            onChange={(e) => setMarkdown(e.target.value)}
            className="flex-1 w-full h-full min-h-[500px] resize-none border-0 rounded-none font-mono text-sm focus-visible:ring-0 focus-visible:ring-offset-0 p-6"
            placeholder="Escreva o conteúdo em Markdown..."
          />
        ) : (
          <DocsRichEditor ref={richEditorRef} initialMarkdown={markdown} onChange={setMarkdown} />
        )}
      </div>
    </div>
  );
}
